import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Heart,
  GraduationCap,
  Sparkles,
  CheckCircle2,
  CreditCard,
  IndianRupee,
  Phone,
  Mail,
  Send,
  Users,
  BookOpen,
  Home as HomeIcon,
  Download,
  ExternalLink,
  Quote,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  getDownloads,
  getImportantLinks,
  getSponsorships,
  getTestimonials,
  postDonationEnquiry,
} from "../lib/api";

const TIERS = [
  { amount: 501, label: "₹501", desc: "Books for one student" },
  { amount: 1001, label: "₹1,001", desc: "One month of fees" },
  { amount: 5001, label: "₹5,001", desc: "Half-year sponsorship" },
  { amount: 12000, label: "₹12,000", desc: "Full year of education" },
];

const IMPACT = [
  { icon: GraduationCap, title: "70%", text: "of our students come from underprivileged backgrounds" },
  { icon: BookOpen, title: "₹12,000", text: "covers a full year of fees, books and exam costs for 1 girl" },
  { icon: Users, title: "1,200+", text: "girls currently enrolled — your help can change their lives" },
];

export default function Support() {
  const [sponsorships, setSponsorships] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [importantLinks, setImportantLinks] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    amount: "",
    purpose: "General support",
    sponsorship_id: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getSponsorships().then(setSponsorships).catch(() => {});
    getDownloads().then(setDownloads).catch(() => {});
    getImportantLinks().then(setImportantLinks).catch(() => {});
    getTestimonials().then(setTestimonials).catch(() => {});
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await postDonationEnquiry({
        ...form,
        amount: form.amount ? parseInt(form.amount, 10) : 0,
      });
      setSuccess(true);
      toast.success("Thank you! We'll reach out to you within 24 hours with payment instructions.");
      setForm({
        name: "",
        email: "",
        phone: "",
        amount: "",
        purpose: "General support",
        sponsorship_id: "",
        message: "",
      });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const pickTier = (amount) => setForm({ ...form, amount: String(amount) });
  const pickStudent = (s) =>
    setForm({
      ...form,
      sponsorship_id: s.id,
      amount: String(s.amount_needed),
      purpose: `Sponsor ${s.name}`,
    });

  return (
    <div data-testid="support-page">
      <PageHeader
        eyebrow="A way to give back"
        title="Help a girl complete her education."
        subtitle="Most of our students come from underprivileged families. A small contribution from you can change a young woman's life — covering fees, books, travel and exam costs."
      />

      {/* Why Support */}
      <section className="py-16 lg:py-24 bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="eyebrow mb-2">Why your support matters</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-primary leading-tight mb-5">
              For many of our students, <span className="text-brand-secondary italic">this college is their only way forward.</span>
            </h2>
            <p className="text-brand-ink/80 leading-relaxed mb-4">
              Our college has been the lamp of education for the daughters of
              Gorakhpur since 1973 — particularly for girls from minority and
              economically weaker sections. Every year, several bright students
              risk dropping out simply because they cannot afford fees, books or
              even bus fare.
            </p>
            <p className="text-brand-ink/75 leading-relaxed">
              Your gift — however small — directly funds a girl's tuition,
              books, exam fees, and sometimes a bicycle to reach the campus.
              You are not just supporting an education; you're supporting a
              future leader, teacher, doctor, or entrepreneur.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid gap-4"
          >
            {IMPACT.map((b) => (
              <div
                key={b.title}
                className="bg-white rounded-3xl p-6 lg:p-7 border border-brand-primary/10 shadow-[0_8px_30px_rgba(15,76,58,0.05)] flex gap-5 items-start"
                data-testid={`impact-${b.title.toLowerCase()}`}
              >
                <div className="w-12 h-12 rounded-full bg-brand-secondary text-brand-primary flex items-center justify-center flex-shrink-0">
                  <b.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display text-3xl text-brand-primary mb-1">{b.title}</div>
                  <p className="text-sm text-brand-ink/75 leading-relaxed">{b.text}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Sponsorship Cases */}
      {sponsorships.length > 0 && (
        <section className="py-16 lg:py-24 bg-brand-accent/40" data-testid="sponsorships-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 lg:mb-14 max-w-2xl">
              <div className="eyebrow mb-2">Sponsor a Student</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary leading-tight">
                Be a guardian to one of these bright minds.
              </h2>
              <p className="text-brand-ink/70 text-sm leading-relaxed mt-3">
                These representative profiles reflect the real circumstances of our students. Names are anonymized for privacy. Pick a student you'd like to sponsor — we'll connect you with the principal's office to arrange the sponsorship.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6" data-testid="sponsorships-grid">
              {sponsorships.map((s, i) => (
                <motion.div
                  key={s.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white rounded-3xl overflow-hidden border border-brand-primary/10 lift-card flex flex-col"
                  data-testid={`sponsorship-${i}`}
                >
                  <div className="p-5 lg:p-6 flex-1 flex flex-col">
                    <div className="w-12 h-12 rounded-full bg-brand-accent text-brand-primary flex items-center justify-center mb-4">
                      <Heart className="w-5 h-5" />
                    </div>
                    <h3 className="font-serif text-xl text-brand-primary mb-1 leading-tight">
                      {s.name}
                    </h3>
                    <div className="text-xs uppercase tracking-[0.2em] text-brand-muted mb-3">
                      {s.course}
                    </div>
                    <p className="text-sm text-brand-ink/75 leading-relaxed mb-4 flex-1 line-clamp-5">
                      {s.story}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-brand-primary/10">
                      <div>
                        <div className="text-xs text-brand-muted">Needs</div>
                        <div className="font-serif text-2xl text-brand-primary">
                          ₹{s.amount_needed.toLocaleString("en-IN")}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          pickStudent(s);
                          document
                            .querySelector("[data-testid=donation-form]")
                            ?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                        data-testid={`sponsor-btn-${i}`}
                        className="bg-brand-primary text-white text-xs font-semibold tracking-wider uppercase rounded-full px-4 py-2 hover:bg-brand-secondary hover:text-brand-primary transition-colors"
                      >
                        Sponsor
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Donation Tiers + Form */}
      <section className="py-16 lg:py-24 bg-brand-ivory" data-testid="donation-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Left: Pick amount + bank info */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="eyebrow mb-2">Choose an amount</div>
              <h2 className="font-serif text-3xl text-brand-primary leading-tight">
                Every rupee matters.
              </h2>
              <p className="text-sm text-brand-ink/70 leading-relaxed mt-2">
                Quickly select a tier or enter a custom amount in the form.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3" data-testid="donation-tiers">
              {TIERS.map((t) => {
                const active = String(t.amount) === form.amount;
                return (
                  <button
                    key={t.amount}
                    onClick={() => pickTier(t.amount)}
                    data-testid={`tier-${t.amount}`}
                    className={`text-left p-4 rounded-2xl border-2 transition-all ${
                      active
                        ? "bg-brand-primary text-white border-brand-primary"
                        : "bg-white border-brand-primary/15 hover:border-brand-secondary"
                    }`}
                  >
                    <div className={`font-display text-2xl ${active ? "text-brand-secondary" : "text-brand-primary"}`}>
                      {t.label}
                    </div>
                    <div className={`text-xs ${active ? "text-white/80" : "text-brand-muted"}`}>
                      {t.desc}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Bank / UPI */}
            <div className="bg-brand-primary text-white rounded-3xl p-6 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-4 h-4 text-brand-secondary" />
                <span className="text-xs uppercase tracking-[0.2em] text-brand-secondary font-semibold">
                  Direct transfer details
                </span>
              </div>
              <div className="text-sm space-y-1">
                <div className="text-white/60 text-xs">Account Name</div>
                <div className="font-medium">Manager, J.A.S. Imambara Girls P.G. College</div>
              </div>
              <div className="text-sm space-y-1">
                <div className="text-white/60 text-xs">Account Number</div>
                <div className="font-mono">XXXXX-XXXX-XXXX (please request from college office)</div>
              </div>
              <div className="text-sm space-y-1">
                <div className="text-white/60 text-xs">Bank / IFSC</div>
                <div className="font-mono">To be shared upon enquiry</div>
              </div>
              <div className="text-sm space-y-1 pt-2 border-t border-white/10">
                <div className="text-white/60 text-xs">UPI ID (Principal)</div>
                <div className="font-mono">jas-imambara@upi <span className="text-white/50 text-xs">(placeholder)</span></div>
              </div>
              <p className="text-xs text-white/70 pt-2 leading-relaxed">
                For confirmed bank/UPI details and 80G receipts, please complete the form on the right or call <a href="tel:+919839417577" className="text-brand-secondary underline">+91-98394-17577</a>.
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <form
            onSubmit={onSubmit}
            data-testid="donation-form"
            className="lg:col-span-3 bg-white rounded-3xl border border-brand-primary/10 shadow-[0_8px_30px_rgba(15,76,58,0.06)] p-6 lg:p-10 space-y-5"
          >
            {success ? (
              <div className="min-h-[400px] flex flex-col items-center justify-center text-center" data-testid="donation-success">
                <div className="w-16 h-16 rounded-full bg-brand-secondary/20 text-brand-primary flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-serif text-2xl text-brand-primary mb-2">Thank you for your support!</h3>
                <p className="text-brand-muted text-sm max-w-sm mb-6">
                  We've received your details. The principal's office will contact you within 24 hours with the next steps and a tax receipt where applicable.
                </p>
                <button
                  type="button"
                  onClick={() => setSuccess(false)}
                  data-testid="donate-another-btn"
                  className="btn-outline"
                >
                  Submit Another Enquiry
                </button>
              </div>
            ) : (
              <>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-brand-secondary" />
                    <span className="text-xs uppercase tracking-[0.2em] text-brand-secondary font-semibold">
                      Donation enquiry
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl lg:text-3xl text-brand-primary leading-tight">
                    Tell us how you'd like to help.
                  </h3>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="d-name">Your Name *</Label>
                    <Input id="d-name" name="name" data-testid="d-name" value={form.name} onChange={onChange} placeholder="Full name" required className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="d-phone">Phone *</Label>
                    <Input id="d-phone" name="phone" data-testid="d-phone" value={form.phone} onChange={onChange} placeholder="+91 9XXXXXXXXX" required className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="d-email">Email *</Label>
                  <Input id="d-email" name="email" type="email" data-testid="d-email" value={form.email} onChange={onChange} placeholder="you@example.com" required className="mt-1" />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="d-amount">Amount (₹)</Label>
                    <div className="relative mt-1">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-muted pointer-events-none" />
                      <Input id="d-amount" name="amount" type="number" data-testid="d-amount" value={form.amount} onChange={onChange} placeholder="e.g. 1001" className="pl-9" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="d-purpose">Purpose</Label>
                    <Input id="d-purpose" name="purpose" data-testid="d-purpose" value={form.purpose} onChange={onChange} placeholder="General / Sponsor a student" className="mt-1" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="d-msg">Message (optional)</Label>
                  <Textarea id="d-msg" name="message" data-testid="d-message" value={form.message} onChange={onChange} placeholder="Any specific instruction, dedication, or question..." rows={4} className="mt-1" />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 pt-3 border-t border-brand-primary/10">
                  <div className="text-xs text-brand-muted flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> <a href="tel:+919839417577" className="hover:text-brand-primary">+91-98394-17577</a></span>
                    <span className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> <a href="mailto:principaljasigpgcollegegkp@gmail.com" className="hover:text-brand-primary">principal@…gkp</a></span>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="d-submit"
                    className="btn-brand disabled:opacity-60"
                  >
                    {submitting ? "Submitting..." : (
                      <>Submit Enquiry <Send className="w-4 h-4" /></>
                    )}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </section>

      {(downloads.length > 0 || importantLinks.length > 0) && (
        <section className="py-16 lg:py-24 bg-brand-accent/40" data-testid="resources-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 lg:gap-12">
            {downloads.length > 0 && (
              <div>
                <div className="eyebrow mb-2">Downloads</div>
                <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary leading-tight mb-6">
                  Forms, prospectus and academic documents.
                </h2>
                <div className="space-y-3">
                  {downloads.map((item) => (
                    <a
                      key={item.id}
                      href={item.file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="bg-white rounded-2xl p-5 border border-brand-primary/10 flex items-start gap-4 hover:border-brand-secondary transition-colors"
                    >
                      <div className="w-11 h-11 rounded-full bg-brand-primary text-brand-secondary flex items-center justify-center flex-shrink-0">
                        <Download className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-serif text-xl text-brand-primary leading-tight">
                          {item.title}
                        </div>
                        {item.category && (
                          <div className="text-xs uppercase tracking-[0.2em] text-brand-muted mt-1">
                            {item.category}
                          </div>
                        )}
                        {item.description && (
                          <p className="text-sm text-brand-ink/70 leading-relaxed mt-2">
                            {item.description}
                          </p>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {importantLinks.length > 0 && (
              <div>
                <div className="eyebrow mb-2">Important Links</div>
                <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary leading-tight mb-6">
                  Useful portals and official resources.
                </h2>
                <div className="grid gap-3">
                  {importantLinks.map((item) => (
                    <a
                      key={item.id}
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className="bg-white rounded-2xl p-5 border border-brand-primary/10 flex items-start justify-between gap-4 hover:border-brand-secondary transition-colors"
                    >
                      <div>
                        <div className="font-serif text-xl text-brand-primary leading-tight">
                          {item.title}
                        </div>
                        {item.description && (
                          <p className="text-sm text-brand-ink/70 leading-relaxed mt-2">
                            {item.description}
                          </p>
                        )}
                      </div>
                      <ExternalLink className="w-5 h-5 text-brand-secondary flex-shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {testimonials.length > 0 && (
        <section className="py-16 lg:py-24 bg-brand-ivory" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 max-w-2xl">
              <div className="eyebrow mb-2">Student Voices</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary leading-tight">
                What students and alumni say.
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {testimonials.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-3xl p-6 border border-brand-primary/10 shadow-[0_8px_30px_rgba(15,76,58,0.05)]"
                >
                  <Quote className="w-8 h-8 text-brand-secondary mb-4" />
                  <p className="text-brand-ink/75 leading-relaxed mb-5">
                    {item.message}
                  </p>
                  <div className="font-serif text-xl text-brand-primary">
                    {item.name}
                  </div>
                  {item.role && (
                    <div className="text-xs uppercase tracking-[0.2em] text-brand-muted mt-1">
                      {item.role}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-12 bg-brand-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HomeIcon className="w-8 h-8 text-brand-secondary mx-auto mb-3" />
          <h2 className="font-serif text-2xl sm:text-3xl mb-2">
            Want to fund a specific girl's entire education?
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-5 leading-relaxed">
            Reach out directly to our Principal Dr. Shagufta Aman, and we'll arrange a personal conversation about how your contribution will be used.
          </p>
          <a
            href="tel:+919839417577"
            data-testid="cta-call-principal"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium bg-brand-secondary text-brand-primary hover:bg-white transition-all"
          >
            <Phone className="w-4 h-4" /> Call the Principal
          </a>
        </div>
      </section>
    </div>
  );
}
